/**
 * Teemavahetuse ring-laine: uus teema laieneb ringina klikikohast (lüliti
 * nupust) üle terve ekraani. View Transitions API teeb vana vaate snapshoti,
 * me animeerime uue vaate clip-path'i ringina. Ilma API-ta (vanemad brauserid)
 * või "vähenda liikumist" eelistuse korral vahetub teema kohe.
 * CSS-pool (vaikimisi cross-fade'i mahakeeramine) on theme.scss-is.
 */

export interface ThemeChangeOrigin {
    x: number
    y: number
}

export const THEME_TRANSITION_MS = 450

type TransitionDocument = Document & {
    startViewTransition?: (update: () => void) => { ready: Promise<void> }
}

/** Ringi raadius, mis katab klikikohast kaugeima nurga. */
export const ringRadius = (origin: ThemeChangeOrigin, width: number, height: number): number =>
    Math.hypot(Math.max(origin.x, width - origin.x), Math.max(origin.y, height - origin.y))

export const canAnimateThemeChange = (env: {
    hasViewTransition: boolean
    reducedMotion: boolean
}): boolean => env.hasViewTransition && !env.reducedMotion

const prefersReducedMotion = (): boolean =>
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Rakendab teemamuutuse (`apply`) kas animeeritult või kohe. `origin` on
 * lüliti nupu keskpunkt viewporti koordinaatides; ilma selleta (nt serveri
 * sünk) animatsiooni ei tehta.
 */
export const animateThemeChange = (apply: () => void, origin?: ThemeChangeOrigin): void => {
    if (typeof document === 'undefined') {
        apply()
        return
    }
    const doc = document as TransitionDocument
    const ok =
        origin !== undefined &&
        canAnimateThemeChange({
            hasViewTransition: typeof doc.startViewTransition === 'function',
            reducedMotion: prefersReducedMotion(),
        })
    if (!ok || !doc.startViewTransition) {
        apply()
        return
    }
    const root = doc.documentElement
    const radius = ringRadius(origin, root.clientWidth, root.clientHeight)
    let transition: { ready: Promise<void> }
    try {
        transition = doc.startViewTransition(apply)
    } catch {
        // Brauser keeldus (nt juba käiv transition): teema vahetub ilma animatsioonita.
        apply()
        return
    }
    // `ready` rejectib, kui dokument on peidus või transition katkestati — teema on
    // selleks hetkeks juba rakendatud, animatsioon lihtsalt jääb ära.
    void transition.ready
        .then(() => {
            root.animate(
                {
                    clipPath: [
                        `circle(0px at ${origin.x}px ${origin.y}px)`,
                        `circle(${radius}px at ${origin.x}px ${origin.y}px)`,
                    ],
                },
                {
                    duration: THEME_TRANSITION_MS,
                    easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
                    pseudoElement: '::view-transition-new(root)',
                },
            )
        })
        .catch(() => undefined)
}
